"use client";

import { useEffect, useState } from "react";

// ----------------------------------------------------------------------
// Id of the section the reader is currently in — what the fiche anchor pills
// highlight. Deriving it from the scroll position rather than from the last
// click is what keeps the in-flow pills and the sticky ones in sync.
// ----------------------------------------------------------------------

/**
 * @param ids     Anchor ids to watch. Absent ones are ignored, so a section the
 *                page drops (an empty "recommended", say) simply never lights up.
 * @param offset  The reading line, measured from the top of the viewport: it has
 *                to clear the sticky header or the section above stays highlighted.
 */
export default function useActiveSection(ids: readonly string[], offset = 140): string {
  const [activeId, setActiveId] = useState(ids[0] ?? "");
  // An array literal would re-run the effect on every render; a string key can't.
  const idsKey = ids.join("|");

  useEffect(() => {
    const sectionIds = idsKey.split("|");
    let frame: number | null = null;

    const read = () => {
      frame = null;

      /* Measured per pass, and sorted by position rather than trusting the id
         order — the pills list them by importance, the page lays them out in
         its own order (financements is last in the DOM, second in the pills). */
      const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((element): element is HTMLElement => element !== null)
        .map((element) => ({ id: element.id, top: element.getBoundingClientRect().top }))
        .sort((a, b) => a.top - b.top);

      if (sections.length === 0) return;

      // Sorted ascending, so the last one past the reading line is the current.
      setActiveId(
        sections.reduce(
          (current, section) => (section.top - offset <= 0 ? section.id : current),
          sections[0].id,
        ),
      );
    };

    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(read);
    };

    schedule(); // first read on the next frame — never synchronously in the effect
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [idsKey, offset]);

  return activeId;
}
